import { createScope, effect, onCleanup, signal, untrack, type ReadonlySignal } from '@zyra/core';
import type { IntrinsicElements as TypedIntrinsics } from './types.js';
export type { HTMLAttributes, ElementAttributes, CSSProperties } from './types.js';
export type Child = Node | string | number | boolean | null | undefined | Child[] | (() => Child);
export type Component<P = Record<string, unknown>> = (props: P) => Child;
export type Props = Record<string, unknown>;
const svgTags = new Set(['svg', 'path', 'circle', 'rect', 'g', 'line', 'polyline', 'polygon', 'ellipse', 'defs', 'use', 'text', 'linearGradient', 'radialGradient', 'stop', 'clipPath', 'mask', 'symbol']);
const exits = new WeakMap<Element, () => Promise<unknown>>();
const layouts = new WeakMap<Element, (previous: DOMRect) => void>();
type HydrationFrame = { parent: Node; next: Node | null; end: Node | null };
let hydration: HydrationFrame | undefined;
const adopted = new WeakSet<Node>();
const adoptedFragments = new WeakSet<Node>();
export class HydrationMismatchError extends Error { constructor(message: string) { super(`Hydration mismatch: ${message}`); } }
function takeHydrated(test: (node: Node) => boolean, description: string): Node {
  const frame = hydration!, node = frame.next;
  if (!node || node === frame.end || !test(node)) throw new HydrationMismatchError(`expected ${description}`);
  frame.next = node.nextSibling; adopted.add(node); return node;
}
function takeComment(label: string): Comment {
  return takeHydrated(node => node.nodeType === 8 && node.nodeValue === label, `<!--${label}-->`) as Comment;
}
function matchingEnd(start: Node, opening: string, closing: string): Node {
  let depth = 0;
  for (let node = start.nextSibling; node; node = node.nextSibling) {
    if (node.nodeType !== 8) continue;
    if (node.nodeValue === opening || (opening === 'row:' && node.nodeValue?.startsWith('row:'))) depth++;
    if (node.nodeValue === closing) { if (!depth) return node; depth--; }
  }
  throw new HydrationMismatchError(`missing <!--${closing}-->`);
}
function finishHydration(frame: HydrationFrame): void {
  if (frame.next !== frame.end) throw new HydrationMismatchError('unexpected remaining children');
}
export function registerExit(element: Element, exit: () => Promise<unknown>): void { exits.set(element, exit); }
export function registerLayout(element: Element, update: (previous: DOMRect) => void): void {
  layouts.set(element, update); onCleanup(() => layouts.delete(element));
}
function elements(node: Node): Element[] { return node instanceof Element ? [node, ...node.querySelectorAll('*')] : []; }
function removeNode(node: Node): void {
  const animated = elements(node).filter(element => exits.has(element));
  if (!animated.length || !(node instanceof HTMLElement) || !node.isConnected) { node.parentNode?.removeChild(node); return; }
  const rect = node.getBoundingClientRect();
  // Exit remnants leave the reactive region immediately and are never keyboard/screen-reader targets.
  node.inert = true; node.setAttribute('aria-hidden', 'true');
  Object.assign(node.style, { position: 'fixed', left: `${rect.left}px`, top: `${rect.top}px`, width: `${rect.width}px`, height: `${rect.height}px`, margin: '0', pointerEvents: 'none', zIndex: '50' });
  document.body.append(node);
  const pending = animated.map(element => { const exit = exits.get(element)!; exits.delete(element); return Promise.resolve().then(exit); });
  void Promise.allSettled(pending).then(() => node.remove());
}
function measureLayouts(nodes: Iterable<Node>): Map<Element, DOMRect> {
  const measured = new Map<Element, DOMRect>();
  for (const node of nodes) for (const element of elements(node)) if (layouts.has(element) && element.isConnected) measured.set(element, element.getBoundingClientRect());
  return measured;
}
export function layoutTransition<T>(root: Element, change: () => T): T {
  const previous = measureLayouts([root]);
  const result = change();
  previous.forEach((rect, element) => { if (element.isConnected) layouts.get(element)?.(rect); });
  return result;
}

function clearBetween(start: Node, end: Node): void {
  const removing: Node[] = [];
  for (let node = start.nextSibling; node && node !== end; node = node.nextSibling) removing.push(node);
  removing.forEach(removeNode);
}
function append(parent: Node, child: Child, before: Node | null = null): void {
  if (hydration) parent = hydration.parent;
  if (child == null || typeof child === 'boolean') return;
  if (Array.isArray(child)) { for (const item of child) append(parent, item, before); return; }
  if (typeof child === 'function') {
    if (hydration) {
      const outer = hydration;
      const start = takeComment('zyra'), end = matchingEnd(start, 'zyra', '/zyra');
      outer.next = end.nextSibling;
      let initial = true;
      effect(() => {
        if (initial) {
          const frame: HydrationFrame = { parent, next: start.nextSibling, end };
          hydration = frame;
          try { const value = child(); untrack(() => append(parent, value, end)); finishHydration(frame); initial = false; }
          finally { hydration = outer; }
        } else {
          const previous = hydration; hydration = undefined;
          try { clearBetween(start, end); const value = child(); untrack(() => append(parent, value, end)); }
          finally { hydration = previous; }
        }
      });
      return;
    }
    const start = document.createComment('zyra'), end = document.createComment('/zyra');
    parent.insertBefore(start, before); parent.insertBefore(end, before);
    effect(() => {
      clearBetween(start, end);
      const value = child();
      untrack(() => append(end.parentNode!, value, end));
    });
  } else if (hydration) {
    if (child instanceof Node) {
      if (adopted.has(child) || adoptedFragments.has(child)) return;
      throw new HydrationMismatchError('unmanaged DOM node; use compiled elements');
    }
    const text = String(child);
    if (!text) return;
    const next = hydration.next;
    if (next?.nodeType === 3 && next.nodeValue?.startsWith(text) && next.nodeValue.length > text.length) (next as Text).splitText(text.length);
    takeHydrated(node => node.nodeType === 3 && node.nodeValue === text, 'matching text');
  } else parent.insertBefore(child instanceof Node ? child : document.createTextNode(String(child)), before);
}
const forbidden = /^(?:innerHTML|outerHTML|srcdoc|textContent|children)$/i;
export function setProp(element: Element, name: string, value: unknown): void {
  if (forbidden.test(name)) throw new Error(`${name} is not a safe binding; use text children`);
  if (/^on/i.test(name)) throw new Error('Event attributes must use a function handler');
  if (name === 'className') name = 'class';
  if (name === 'htmlFor') name = 'for';
  if ((name.startsWith('aria-') || name.startsWith('data-')) && typeof value === 'boolean') value = String(value);
  if (['href', 'src', 'action', 'formaction', 'xlink:href'].includes(name.toLowerCase()) && typeof value === 'string') {
    const normalized = value.replace(/[\u0000-\u0020]/g, '');
    if (/^(javascript|vbscript|data):/i.test(normalized)) throw new Error(`Unsafe URL in ${name}`);
  }
  if (name === 'style' && value && typeof value === 'object') {
    const style = (element as HTMLElement).style;
    style.cssText = '';
    for (const [key, entry] of Object.entries(value)) {
      if (key.startsWith('--') || key.includes('-')) style.setProperty(key, String(entry ?? ''));
      else (style as unknown as Record<string, unknown>)[key] = entry == null ? '' : String(entry);
    }
    return;
  }
  if (value == null || value === false) {
    element.removeAttribute(name);
    if (name === 'checked' || name === 'disabled' || name === 'selected') (element as unknown as Props)[name] = false;
    if (name === 'value') (element as HTMLInputElement).value = '';
  } else if (['value', 'checked', 'selected', 'disabled', 'multiple', 'muted'].includes(name) && name in element) {
    if ((element as unknown as Props)[name] !== value) (element as unknown as Props)[name] = value;
  } else element.setAttribute(name, value === true ? '' : String(value));
}

/** Compiler target. Getters preserve reactive reads; components execute once per mount. */
export function h(type: string | ((props: any) => Child), props: Props | null = null): Child {
  props ??= {};
  if (typeof type === 'function') return untrack(() => type(props));
  if (type.toLowerCase() === 'script') throw new Error('Script elements are not supported by the safe DOM renderer');
  const frame = hydration;
  const element = frame ? takeHydrated(node => node instanceof Element && node.localName === type, `<${type}>`) as Element
    : svgTags.has(type) ? document.createElementNS('http://www.w3.org/2000/svg', type) : document.createElement(type);
  const propertyAttribute = (name: string) => name === 'className' ? 'class' : name === 'htmlFor' ? 'for' : name;
  const expectedAttributes = new Set(Object.keys(props).filter(name => name !== 'ref' && name !== 'children' && !/^on[A-Z]/.test(name)).map(name => propertyAttribute(name).toLowerCase()));
  if (frame) for (const name of element.getAttributeNames()) if (!expectedAttributes.has(name.toLowerCase())) throw new HydrationMismatchError(`unexpected attribute ${name}`);
  for (const name of Object.keys(props)) {
    if (name === 'children') continue;
    if (name === 'ref') { const ref = props[name]; if (typeof ref === 'function') ref(element); continue; }
    if (/^on[A-Z]/.test(name)) {
      const handler = props[name];
      if (typeof handler !== 'function') throw new Error(`${name} must be a function`);
      const event = name === 'onDoubleClick' ? 'dblclick' : name.slice(2).toLowerCase();
      const listener = (event: Event) => untrack(() => handler(event));
      element.addEventListener(event, listener);
      onCleanup(() => element.removeEventListener(event, listener));
    } else {
      let initial = true;
      effect(() => {
        const value = props![name];
        if (frame && initial) {
          const probe = svgTags.has(type) ? document.createElementNS('http://www.w3.org/2000/svg', type) : document.createElement(type);
          setProp(probe, name, value);
          const attribute = propertyAttribute(name);
          const expected = name === 'value' ? value == null || value === false ? null : String(value)
            : ['checked', 'selected', 'disabled', 'multiple', 'muted'].includes(name) ? value ? '' : null : probe.getAttribute(attribute);
          if (name === 'style') {
            if ((element as HTMLElement).style.cssText !== (probe as HTMLElement).style.cssText) throw new HydrationMismatchError(`style on <${type}>`);
          } else if (element.getAttribute(attribute) !== expected) throw new HydrationMismatchError(`${attribute} on <${type}>`);
          initial = false;
          // Preserve edits made before JavaScript loads; later reactive writes remain controlled.
          if (name === 'value' && element instanceof HTMLInputElement && element.value !== element.defaultValue) return;
          if (name === 'checked' && element instanceof HTMLInputElement && element.checked !== element.defaultChecked) return;
        }
        setProp(element, name, value);
      });
    }
  }
  if (frame) {
    const nested: HydrationFrame = { parent: element, next: element.firstChild, end: null }; hydration = nested;
    try { append(element, props.children as Child); finishHydration(nested); } finally { hydration = frame; }
  } else append(element, props.children as Child);
  return element;
}
export function mount(component: () => Child, container: Element): () => void {
  const scope = createScope();
  const start = document.createComment('root'), end = document.createComment('/root');
  container.append(start, end);
  try { scope.run(() => append(container, component(), end)); }
  catch (error) { scope.dispose(); clearBetween(start, end); start.remove(); end.remove(); throw error; }
  return () => { scope.dispose(); clearBetween(start, end); start.remove(); end.remove(); };
}
/** Verify and adopt server nodes. Replacement is an explicit application policy. */
export function hydrate(component: () => Child, container: Element, options: {
  mismatch?: 'throw' | 'replace'; onMismatch?: (error: HydrationMismatchError) => void;
} = {}): () => void {
  const scope = createScope(), previous = hydration;
  let start: Comment, end: Node;
  try {
    hydration = { parent: container, next: container.firstChild, end: null };
    start = takeComment('root'); end = matchingEnd(start, 'root', '/root');
    if (end.nextSibling) throw new HydrationMismatchError('unexpected nodes after root');
    const frame: HydrationFrame = { parent: container, next: start.nextSibling, end }; hydration = frame;
    scope.run(() => append(container, component(), end)); finishHydration(frame);
    return () => { scope.dispose(); clearBetween(start, end); start.remove(); end.parentNode?.removeChild(end); };
  } catch (error) {
    scope.dispose();
    if (!(error instanceof HydrationMismatchError)) throw error;
    options.onMismatch?.(error);
    if (options.mismatch !== 'replace') throw error;
    hydration = undefined;
    container.replaceChildren(); return mount(component, container);
  } finally { hydration = previous; }
}
export function Show(props: { when: unknown; children?: Child; fallback?: Child }): Child {
  return () => props.when ? props.children : props.fallback;
}
export function For<T extends object>(props: {
  each: readonly T[] | ReadonlySignal<readonly T[]>;
  key: keyof T | ((item: T) => string | number);
  children: (item: T, index: ReadonlySignal<number>) => Child;
}): Child {
  const fragment = document.createDocumentFragment();
  let end: Node = document.createComment('/for');
  let initialHydration = hydration;
  if (initialHydration) adoptedFragments.add(fragment); else fragment.append(end);
  type Row = { item: ReturnType<typeof signal<T>>; index: ReturnType<typeof signal<number>>; nodes: Node[]; dispose: () => void };
  const rows = new Map<unknown, Row>();
  const listScope = createScope();
  onCleanup(() => { for (const row of rows.values()) row.dispose(); rows.clear(); });
  const render = untrack(() => props.children);
  effect(() => {
    const source = props.each;
    const items = Array.isArray(source) ? source as readonly T[] : (source as ReadonlySignal<readonly T[]>).value;
    const keys = items.map(item => typeof props.key === 'function' ? props.key(item) : item[props.key]);
    if (keys.some(key => typeof key !== 'string' && typeof key !== 'number')) throw new Error('For keys must be strings or numbers');
    if (new Set(keys).size !== keys.length) throw new Error('For keys must be unique');
    untrack(() => {
      const previous = measureLayouts([...rows.values()].flatMap(row => {
        const nodes: Node[] = []; let node: Node | null = row.nodes[0], last = row.nodes[row.nodes.length - 1];
        while (node) { nodes.push(node); if (node === last) break; node = node.nextSibling; }
        return nodes;
      }));
      const wanted = new Set<unknown>(keys);
      for (const [key, row] of rows) if (!wanted.has(key)) {
        row.dispose();
        let node: Node | null = row.nodes[0];
        const last = row.nodes[row.nodes.length - 1];
        while (node) { const next: Node | null = node.nextSibling; removeNode(node); if (node === last) break; node = next; }
        rows.delete(key);
      }
      items.forEach((item, index) => {
        const key = keys[index];
        let row = rows.get(key);
        if (!row) {
          const scope = listScope.run(() => createScope()), state = signal(item), position = signal(index);
          const proxy = new Proxy({} as T, {
            get: (_, key) => Reflect.get(state.value, key),
            ownKeys: () => Reflect.ownKeys(state.value),
            getOwnPropertyDescriptor: () => ({ enumerable: true, configurable: true })
          });
          const holder = document.createDocumentFragment();
          const label = `row:${encodeURIComponent(String(key)).replaceAll('-', '%2D')}`;
          let nodes: Node[];
          if (initialHydration) {
            const outer = hydration!, first = takeComment(label), last = matchingEnd(first, 'row:', '/row');
            const nested: HydrationFrame = { parent: outer.parent, next: first.nextSibling, end: last };
            hydration = nested;
            try { scope.run(() => append(holder, render(proxy, position))); finishHydration(nested); }
            finally { hydration = outer; outer.next = last.nextSibling; }
            nodes = [first, last];
          } else {
            holder.append(document.createComment(label));
            scope.run(() => append(holder, render(proxy, position)));
            holder.append(document.createComment('/row')); nodes = [...holder.childNodes];
          }
          row = { item: state, index: position, nodes, dispose: scope.dispose };
          rows.set(key, row);
        } else { row.item.value = item; row.index.value = index; }
      });
      if (initialHydration) { end = takeComment('/for'); initialHydration = undefined; }
      // Walk backwards, moving only ranges whose position changed. Ordinary edits preserve focus.
      let anchor: Node = end;
      for (let index = keys.length - 1; index >= 0; index--) {
        const row = rows.get(keys[index])!;
        const first = row.nodes[0], last = row.nodes[row.nodes.length - 1];
        if (last.nextSibling !== anchor) {
          const focused = document.activeElement as HTMLElement | null;
          const selection = focused instanceof HTMLInputElement && ['text', 'search', 'url', 'tel', 'password'].includes(focused.type)
            ? [focused.selectionStart, focused.selectionEnd] as const : null;
          const moving = document.createDocumentFragment();
          let node: Node | null = first;
          while (node) { const next: Node | null = node.nextSibling; moving.append(node); if (node === last) break; node = next; }
          anchor.parentNode!.insertBefore(moving, anchor);
          if (focused && focused !== document.activeElement && focused.isConnected) {
            focused.focus({ preventScroll: true });
            if (selection) (focused as HTMLInputElement).setSelectionRange(...selection);
          }
        }
        anchor = first;
      }
      previous.forEach((rect, element) => { if (element.isConnected) layouts.get(element)?.(rect); });
    });
  });
  return fragment;
}
export function Portal(props: { target: Element; children?: Child }): Child {
  const start = document.createComment('portal'), end = document.createComment('/portal');
  props.target.append(start, end);
  const previous = hydration; hydration = undefined;
  try { append(props.target, props.children, end); } finally { hydration = previous; }
  onCleanup(() => { clearBetween(start, end); start.remove(); end.remove(); });
  return null;
}

declare global {
  namespace JSX {
    type Element = Child;
    interface ElementChildrenAttribute { children: unknown }
    interface IntrinsicElements extends TypedIntrinsics {}
  }
}
